import type { ColumnFilter, Row } from "@tanstack/react-table";
import { isFuture } from "date-fns";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";

import { createDeleteSpecialPriceButton } from "src/app/customers/containers/utils";
import { ActionIcon, Button, Confirm, Icon, TableAction, TableActionForSelection } from "src/components";
import { ICON_TABLE_SIZE } from "src/table";
import { Color } from "src/theme";
import type { SpecialPriceListDto } from "src/types";
import { isTodayInDateRange } from "src/utils";

export const createSpecialPriceActionRow =
    (
        t: TFunction,
        openProductPriceFormModal: (specialPriceId: number, customerId: number | undefined, bulkId: number | undefined) => void,
        deleteProductPrice: (specialPriceId: number) => void,
    ) =>
    (row: Row<SpecialPriceListDto>): JSX.Element =>
        (
            <TableAction>
                {(isFuture(new Date(row.original.validFrom)) ||
                    isTodayInDateRange(new Date(row.original.validFrom), new Date(row.original.validTo))) && (
                    <ActionIcon
                        tooltip={row.original.bulkId ? t("specialPrices.editBulkTooltip") : t("specialPrices.editTooltip")}
                        onClick={() => openProductPriceFormModal(row.original.id, row.original.customer.id, row.original.bulkId)}
                        color={Color.accent100}
                    >
                        {row.original.bulkId ? (
                            <Icon.BulkEdit size={ICON_TABLE_SIZE} color={Color.primary600} />
                        ) : (
                            <Icon.Edit size={ICON_TABLE_SIZE} color={Color.primary600} filled />
                        )}
                    </ActionIcon>
                )}
                {isFuture(new Date(row.original.validFrom)) && createDeleteSpecialPriceButton(t, () => deleteProductPrice(row.original.id))}
            </TableAction>
        );

export const overrideSpecialPriceRowProps = (row: Row<SpecialPriceListDto>): Record<string, unknown> => {
    if (row.original.bulkId) {
        return { $backgroundColor: Color.accent100 };
    }
    return { $backgroundColor: Color.white };
};

export const createActionsForSelection =
    (t: TFunction, bulkDelete: (ids: number[]) => void) =>
    (_: boolean, selectedRows: SpecialPriceListDto[], __: ColumnFilter[]): ReactNode => {
        const selectedRowIds = selectedRows.map((row) => row.id) as number[];
        return (
            <TableActionForSelection>
                <Confirm
                    title={t("dashboard.allSpecialPrices.bulkDelete.title")}
                    content={t("dashboard.allSpecialPrices.bulkDelete.content")}
                    confirmButtonTransKey="dashboard.allSpecialPrices.bulkDelete.button"
                    onConfirm={() => {
                        bulkDelete(selectedRowIds);
                    }}
                >
                    {(open) => (
                        <Button disabled={selectedRowIds.length === 0} variant="outline" type="button" onClick={open}>
                            {t("dashboard.allSpecialPrices.bulkDelete.button")}
                        </Button>
                    )}
                </Confirm>
            </TableActionForSelection>
        );
    };
